import React, { useEffect, useMemo, useState } from 'react';
import { View } from 'react-native'
import Text from 'theme/Text'
import _ from "lodash";
import i18n from "utils/i18n";
import useSwipe from "utils/UseSwipe"
import { calendarHeaderStyles, calendarStyles, footerStyles, headerStyles } from 'styles/components/organisms/Calendar'
import HelperString from 'utils/helper-string'
import ThemedPressable from 'components/atoms/ThemedPressable'
import { useSelector } from 'react-redux'
import { dataConstants } from 'services/redux/data/constants'
import { TESTID } from '_shared/config/testID'
import { CalendarViewModes } from 'utils/constants/calendar-modes-constants'
import Icon from 'components/atoms/Icon'
import { IconSystemConstants } from 'utils/constants/IconNameConstants'
import isEqual from 'react-fast-compare'
import HelperDate, { dateFormat } from 'utils/helper-date'
import { useIsSmallDevice } from 'utils/helper-device'

const Calendar = ( { calendarDate = null, minDate, maxDate, dateDots = null, onDayPress = null, onModeChange = null } ) => {
	
	const _calendarViewMode = useSelector( state => {
		return state.data[dataConstants.CALENDAR_VIEW_MODE_BOOKINGS];
	} );
	const isSmallDevice = useIsSmallDevice();
	
	const [ mode, setMode ] = useState( isSmallDevice ? CalendarViewModes.WEEK : _calendarViewMode );
	const [ date, setDate ] = useState( calendarDate !== null ? calendarDate : HelperDate.date() );
	
	const calendarMaxDate = maxDate === undefined ? HelperDate.date().add( 1, 'year' ) : HelperDate.date( maxDate, dateFormat.DATE );
	const calendarMinDate = minDate === undefined ? HelperDate.date().subtract( 1, 'year' ) : HelperDate.date( minDate, dateFormat.DATE );
	
	const onSwipeRight = () => {
		switch( mode ) {
			case CalendarViewModes.MONTH:
				monthPressed( date.subtract( 1, 'month' ).endOf('month') );
				break;
			
			case CalendarViewModes.WEEK:
				monthPressed( date.subtract( 1, 'week' ).endOf('week') );
				break;
		}
	}
	
	const onSwipeLeft = () => {
		switch( mode ) {
			case CalendarViewModes.MONTH:
				monthPressed( date.add( 1, 'month' ).startOf('month') );
				break;
			
			case CalendarViewModes.WEEK:
				monthPressed( date.add( 1, 'week' ).startOf('week') );
				break;
		}
	}
	
	const switchMode = () => {
		const newMode = ( mode === CalendarViewModes.MONTH ? CalendarViewModes.WEEK : CalendarViewModes.MONTH );
		setMode( newMode );
		
		if( _.isFunction( onModeChange ) ){
			onModeChange( newMode );
		}
	}
	
	const weekPressed = ( newDate ) => {
		if( newDate.isBefore( calendarMinDate ) ) {
			newDate = calendarMinDate;
		}
		
		if( newDate.isAfter( calendarMaxDate ) ) {
			newDate = calendarMaxDate;
		}
		
		dayPressed( newDate );
	}
	
	const monthPressed = ( newDate ) => {
		if( newDate.isBefore( calendarMinDate ) ) {
			newDate = calendarMinDate;
		}
		
		if( newDate.isAfter( calendarMaxDate ) ) {
			newDate = calendarMaxDate;
		}
		
		dayPressed( newDate );
	}
	
	const dayPressed = ( newDate ) => {
		if( newDate.isSame( date, 'day' ) ) {
			return;
		}
		
		setDate( newDate );
		
		if( _.isFunction( onDayPress ) ){
			onDayPress( newDate );
		}
	}
	
	const { onTouchStart, onTouchEnd } = useSwipe( onSwipeLeft, onSwipeRight, 2 )
	
	useEffect( () => {
		//setDate( calendarDate );
	}, [ calendarDate ] );
	
	useEffect( () => {
		if( !isSmallDevice ) {
			setMode( _calendarViewMode );
		}
	}, [ _calendarViewMode, isSmallDevice ] )
	
	const headerView = useMemo( () => {
		switch( mode ) {
			case CalendarViewModes.MONTH:
				const previous_month_button = (
					date.subtract( 1, 'month' ).endOf('month').isSameOrAfter( calendarMinDate )
						? <ThemedPressable onPress={ monthPressed.bind( this, date.subtract( 1, 'month' ).endOf('month' ) ) } hitSlop={ 20 } testID={ TESTID.INTERFACE.CALENDAR.PREVIOUS_MONTH_BUTTON }><Icon icon={ IconSystemConstants.CARET_LEFT } style={ [ headerStyles.arrow, headerStyles.arrowLeft ] } /></ThemedPressable>
						: <Icon icon={ IconSystemConstants.CARET_LEFT } style={ [ headerStyles.arrow, headerStyles.arrowLeft,  headerStyles.arrowDisabled ] } />
				);
				
				const next_month_button = (
					date.add( 1, 'month' ).startOf('month').isSameOrBefore( calendarMaxDate )
						? <ThemedPressable onPress={ monthPressed.bind( this, date.add( 1, 'month' ).startOf('month' ) ) } hitSlop={ 20 } testID={ TESTID.INTERFACE.CALENDAR.NEXT_MONTH_BUTTON }><Icon icon={ IconSystemConstants.CARET_RIGHT } style={ [ headerStyles.arrow, headerStyles.arrowRight ] } /></ThemedPressable>
						: <Icon icon={ IconSystemConstants.CARET_RIGHT } style={ [ headerStyles.arrow, headerStyles.arrowRight, headerStyles.arrowDisabled ] } />
				);
				
				return (
					<View
						style={ headerStyles.row }>
						
						<View>{ previous_month_button }</View>
						<View>
							<Text
								style={ headerStyles.title }
								testID={ TESTID.INTERFACE.CALENDAR.MONTH_LABEL }>
								
								{ date.format( 'MMM' ) } { date.format( 'YYYY' ) }
							</Text>
						</View>
						<View>{ next_month_button }</View>
					</View>
				);
			
			case CalendarViewModes.WEEK:
				const previous_week_button = (
					date.subtract( 1, 'week' ).endOf('week').isSameOrAfter( calendarMinDate )
						? <ThemedPressable onPress={ weekPressed.bind( this, date.subtract( 1, 'week' ).startOf('week') ) } hitSlop={ 20 } testID={ TESTID.INTERFACE.CALENDAR.PREVIOUS_WEEK_BUTTON }><Icon icon={ IconSystemConstants.CARET_LEFT } style={ [ headerStyles.arrow, headerStyles.arrowLeft ]} /></ThemedPressable>
						: <Icon icon={ IconSystemConstants.CARET_LEFT } style={ [ headerStyles.arrow, headerStyles.arrowLeft,  headerStyles.arrowDisabled ] } />
				);
				
				const next_week_button = (
					date.add( 1, 'week' ).startOf('week').isSameOrBefore( calendarMaxDate )
						? <ThemedPressable onPress={ weekPressed.bind( this, date.add( 1, 'week' ).startOf('week') ) } hitSlop={ 20 } testID={ TESTID.INTERFACE.CALENDAR.NEXT_WEEK_BUTTON }><Icon icon={ IconSystemConstants.CARET_RIGHT } style={ [ headerStyles.arrow, headerStyles.arrowRight ] } /></ThemedPressable>
						: <Icon icon={ IconSystemConstants.CARET_RIGHT } style={ [ headerStyles.arrow, headerStyles.arrowRight, headerStyles.arrowDisabled ] } />
				);
				
				return (
					<View style={headerStyles.row}>
						<View>{previous_week_button}</View>
						<View>
							<Text
								style={headerStyles.title}
								testID={ TESTID.INTERFACE.CALENDAR.WEEK_LABEL}>
								
								{ i18n.t( 'calendar.week', { week: date.week(), year: date.format( 'YYYY' ) } ) }
							</Text>
						</View>
						<View>{next_week_button}</View>
					</View>
				);
		}
		
		return null;
	}, [ date, mode, calendarMinDate, calendarMaxDate ] )
	
	const footerView = useMemo( () => {
		// Footer
		const i18nLabel = mode === CalendarViewModes.MONTH ? 'calendar.show_week_calendar' :'calendar.show_month_calendar';
		
		return (
			<View
				style={ footerStyles.row }>
				
				<ThemedPressable
					onPress={switchMode}
					testID={ TESTID.INTERFACE.CALENDAR.SWITCH_MODE_BUTTON }>
					
					<Text
						style={ footerStyles.text }>
						
						{ i18n.t( i18nLabel ) }
					</Text>
				</ThemedPressable>
			</View>
		)
	}, [ mode ] )

	const calendarView = useMemo( () => {
		// Get the weekdays
		const weekDaysShort = date.localeData().weekdaysShort();
		
		const days_header = (
			<View
				style={calendarStyles.row }>
				
				{ _.map( [ 1, 2, 3, 4, 5, 6, 0 ], ( day ) => {
					return (
						<View
							key={ HelperString.hash('calendar-days', day ) }
							style={calendarStyles.col}>
							
							<Text
								style={calendarHeaderStyles.day_text}>
								
								{ weekDaysShort[ day ] }
							</Text>
						</View>
					);
				}) }
			</View>
		);
		
		// calculate how many rows we require
		let days_rows = [];
		
		// create all the days we'll need to show
		let dates = [];
		
		switch( mode ) {
			case CalendarViewModes.MONTH:
				const endOfPreviousMonthDay = date.subtract( 1, 'month' ).endOf('month').day();
				const startOfCurrentMonthWeekDay = date.startOf('week').day() - 1;
				const endOfCurrentMonth = date.endOf('month').date();
				const startDayOfNextMonth = date.add( 1, 'month' ).startOf( 'month').day()
				
				const totalDaysToAdd = (endOfPreviousMonthDay - startOfCurrentMonthWeekDay) + endOfCurrentMonth + ( 7 - startDayOfNextMonth );
				let currentActiveDateMonth = date.startOf('month').subtract( endOfPreviousMonthDay - startOfCurrentMonthWeekDay, 'day' );
				for( let i = 0; i <= totalDaysToAdd; i++ ) {
					dates.push( currentActiveDateMonth );
					currentActiveDateMonth = currentActiveDateMonth.add( 1, 'day' );
				}
				break;
				
			case CalendarViewModes.WEEK:
				let currentActiveDateWeek = date.startOf('week');
				for( let day_nr = 1; day_nr <= 7; day_nr++ ) {
					dates.push( currentActiveDateWeek );
					currentActiveDateWeek = currentActiveDateWeek.add( 1, 'day' );
				}
				break;
		}
		
		let days = [];
		for( const currentDate of dates ) {
			if( days.length === 7 ) {
				days_rows.push(
					<DayRow
						key={ HelperString.hash('day-row-items', _.size( days_rows ) ) }
						days={ days }
					/>
				);
				days = [];
			}
			
			days.push(
				<Day
					key={ HelperString.hash('day', currentDate.format( dateFormat.DATE ) ) }
					selectedDate={ date }
					date={ currentDate }
					minDate={ calendarMinDate }
					maxDate={ calendarMaxDate }
					dateDots={ dateDots }
					notThisMonth={ !( mode === CalendarViewModes.WEEK || ( currentDate.isSame( date, 'year' ) && currentDate.isSame( date, 'month' ) ) ) }
					onPress={ dayPressed }
			    />
			)
		}
		
		if( days.length > 0 ) {
			days_rows.push(
				<DayRow
					key={ HelperString.hash('day-row-items' ) }
					days={ days }
				/>
			);
		}
		
		return (
			<>
				{ days_header }
				{ days_rows }
			</>
		);
	}, [ date, mode, dateDots ] )
	
	return (
		<View
			onTouchStart={ onTouchStart }
			onTouchEnd={ onTouchEnd }>
			
			{/*<Text>{ date } - { calendarDate } - { today }</Text>*/}
			{ headerView }
			{ calendarView }
			{ footerView }
		</View>
	)
}

const Day = ({ selectedDate, date, minDate, maxDate, dateDots, onPress, notThisMonth }) => {
	//const year = activeDate.year();
	//const month = activeDate.month();
	const day = date.date();
	
	const items = [];
	
	let isToday = HelperDate.fn.isToday( date );
	let isActive = date.isSame( selectedDate, 'day' );
	let isDisabled = date.isBefore( minDate ) || date.isAfter( maxDate );
	
	const styles = [
		calendarStyles.day
	];
	
	const stylesText = [
		calendarStyles.day_text
	];
	
	switch( true ) {
		case isDisabled:
		case notThisMonth:
			stylesText.push( calendarStyles.day_text_inactive );
			break;
		
		case isActive:
			styles.push( calendarStyles.day_active );
			stylesText.push( calendarStyles.day_text_active );
			break;
		
		case isToday:
			stylesText.push( calendarStyles.day_text_today );
			break;
	}
	
	items.push(
		<View
			key={ HelperString.hash( `day_day`, date.format( dateFormat.DATE ) ) }
			style={ styles } >
			
			<Text style={ stylesText }>{ day }</Text>
		</View>
	);
	
	const dotsForDate = _.get( dateDots, [ date.format( dateFormat.DATE ), 'dots' ], null );
	
	if( dotsForDate !== null ) {
		const dots = [];
		_.forEach( dotsForDate, ( dot, index ) => {
			const dotStyle = [ calendarStyles.dot, { backgroundColor: dot.color } ]
			
			if( index === 3 ) {
				dots.push(
					<View
						key={ HelperString.hash( `dot-spacer-${ date }`, index ) }
						style={ calendarStyles.dotSpacer }
					/>
				)
			}
			
			dots.push(
				<View
					key={ HelperString.hash( `dot-${ date }`, index ) }
					style={ dotStyle }
				/>
			)
		});
		
		items.push(
			<View
				key={ HelperString.hash( `day-dots`, date ) }
				style={ calendarStyles.dots }>
				
				{ dots }
			</View>
		)
	}
	
	if( isDisabled ) {
		return (
			<View
				key={ HelperString.hash( `disabled`, date ) }
				style={ calendarStyles.col }>
				
				{ items }
			</View>
		);
	}
	
	return (
		<View
			key={ HelperString.hash( `day`, date ) }
			style={ calendarStyles.col }>
			
			<ThemedPressable
				onPress={ onPress.bind( this, date ) }
				testID={ `${TESTID.INTERFACE.CALENDAR.DAY_BUTTON}_${ day }` }>
				
				{ items }
			</ThemedPressable>
		</View>
	);
}

const DayRow = ( { days } ) => {
	if( _.isEmpty( days ) ) {
		return null;
	}
	
	const key = _.map( days, o => {
		return o.key;
	} );
	
	return (
		<View
			key={ HelperString.hash( 'day-row', key.join('-') ) }
			style={ calendarStyles.row }>
			
			{ days }
		</View>
	);
}

export default React.memo( Calendar, isEqual );
